import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, Undo2 } from "lucide-react";
import { stopViewAs, useViewAs, viewAsBannerText, viewAsExpired } from "../../lib/viewAs/viewAs";

// The persistent "Viewing as …" banner (D-O115): fixed at the top of every page while an Admin views as a role, with the
// way back. The page below moves down by the banner's height so nothing is hidden. When the hour is up it ends the session
// by itself (audited by the server) and reloads as Admin.

const BANNER_HEIGHT = "2.25rem";

const OFFSET_CSS = `
body { padding-top: ${BANNER_HEIGHT}; }
#app-sidebar { top: ${BANNER_HEIGHT} !important; height: calc(100vh - ${BANNER_HEIGHT}) !important; }
#app-sidebar ~ div > header { top: ${BANNER_HEIGHT} !important; }
`;

/** Ends the view (server audit first) and reloads the application as the real person, at the start page. */
export async function backToAdmin(): Promise<void> {
  await stopViewAs();
  window.location.assign("/");
}

function endsAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function ViewAsBanner() {
  const session = useViewAs();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session) return;
    if (viewAsExpired(session)) {
      void backToAdmin();
      return;
    }
    const ms = Date.parse(session.expiresAtUtc) - Date.now();
    const t = window.setTimeout(() => void backToAdmin(), Math.min(Math.max(ms, 0), 2_147_000_000));
    return () => window.clearTimeout(t);
  }, [session]);

  if (!session) return null;
  const until = endsAt(session.expiresAtUtc);

  return createPortal(
    <>
      <style data-testid="view-as-offset">{OFFSET_CSS}</style>
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-3 text-xs text-amber-950 shadow-sm print:hidden sm:text-sm"
        style={{ height: BANNER_HEIGHT }}
        data-testid="view-as-banner"
      >
        <Eye aria-hidden="true" className="h-4 w-4 flex-none" />
        <span className="min-w-0 truncate">
          <strong className="font-semibold sm:hidden" data-testid="view-as-banner-text-short">
            As {session.roleLabel} · read-only
          </strong>
          <strong className="hidden font-semibold sm:inline" data-testid="view-as-banner-text">
            {viewAsBannerText(session.roleLabel)}
          </strong>
          <span className="hidden sm:inline">. Nothing can be changed{until ? `; ends by ${until}` : ""}.</span>
        </span>
        <button
          type="button"
          className="inline-flex flex-none items-center gap-1 rounded-md border border-amber-400 bg-white px-2 py-0.5 text-xs font-semibold text-amber-950 hover:bg-amber-50 disabled:opacity-60"
          onClick={() => {
            setBusy(true);
            void backToAdmin();
          }}
          disabled={busy}
          data-testid="view-as-stop"
        >
          <Undo2 aria-hidden="true" className="h-3.5 w-3.5" />
          Back to Admin
        </button>
      </div>
    </>,
    document.body
  );
}
